import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import CustomerSelector from './components/CustomerSelector';
import InvoiceDetails from './components/InvoiceDetails';
import LineItemsTable from './components/LineItemsTable';
import TaxCalculationPanel from './components/TaxCalculationPanel';
import PaymentTermsSection from './components/PaymentTermsSection';
import NotesSection from './components/NotesSection';
import InvoicePreview from './components/InvoicePreview';

// Add this block - utility function for class name concatenation
const cn = (...classes) => {
  return classes?.filter(Boolean)?.join(' ');
};

const InvoiceCreation = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Invoice form state
  const [invoiceData, setInvoiceData] = useState({
    customer: null,
    invoiceNumber: `INV-${new Date()?.getFullYear()}-${(Math.floor(Math.random() * 1000) + 1)?.toString()?.padStart(3, '0')}`,
    invoiceDate: new Date()?.toISOString()?.split('T')?.[0],
    dueDate: '',
    poNumber: '',
    lineItems: [
      {
        id: 1,
        description: '',
        quantity: 1,
        rate: 0,
        taxRate: 0,
        amount: 0
      }
    ],
    subtotal: 0,
    taxAmount: 0,
    discountType: 'percentage',
    discountValue: 0,
    discountAmount: 0,
    total: 0,
    paymentTerms: 'net-30',
    notes: '',
    termsAndConditions: ''
  });

  // Sample customer data
  const customerOptions = [
    {
      value: 'acme-corp',
      label: 'Acme Corporation',
      email: 'billing@acme.com',
      phone: '+1 (555) 123-4567',
      address: '123 Business St, Suite 100\nNew York, NY 10001',
      taxNumber: 'TAX123456789'
    },
    {
      value: 'techstart-inc',
      label: 'TechStart Inc.',
      email: 'accounts@techstart.com',
      phone: '+1 (555) 234-5678',
      address: '456 Innovation Ave\nSan Francisco, CA 94102',
      taxNumber: 'TAX987654321'
    },
    {
      value: 'global-solutions',
      label: 'Global Solutions Ltd',
      email: 'finance@globalsolutions.com',
      phone: '+1 (555) 345-6789',
      address: '789 Enterprise Blvd\nChicago, IL 60601',
      taxNumber: 'TAX456789123'
    }
  ];

  // Sample product/service items for autocomplete
  const productItems = [
    {
      id: 1,
      name: 'Web Development Service',
      description: 'Custom website development',
      rate: 150,
      taxRate: 10
    },
    {
      id: 2,
      name: 'Mobile App Development',
      description: 'iOS and Android app development',
      rate: 200,
      taxRate: 10
    },
    {
      id: 3,
      name: 'Consulting Services',
      description: 'Technical consulting and advisory',
      rate: 125,
      taxRate: 10
    },
    {
      id: 4,
      name: 'UI/UX Design',
      description: 'User interface and experience design',
      rate: 100,
      taxRate: 10
    },
    {
      id: 5,
      name: 'Digital Marketing',
      description: 'SEO, SEM and social media marketing',
      rate: 75,
      taxRate: 10
    }
  ];

  // Calculate totals
  const calculateTotals = (items, discount = { type: 'percentage', value: 0 }) => {
    const subtotal = items?.reduce((sum, item) => sum + (item?.amount || 0), 0);
    
    let discountAmount = 0;
    if (discount?.type === 'percentage') {
      discountAmount = (subtotal * (discount?.value || 0)) / 100;
    } else {
      discountAmount = discount?.value || 0;
    }

    const afterDiscount = subtotal - discountAmount;
    const taxAmount = items?.reduce((sum, item) => {
      const itemTax = ((item?.amount || 0) * (item?.taxRate || 0)) / 100;
      return sum + itemTax;
    }, 0);
    
    const total = afterDiscount + taxAmount;

    return {
      subtotal,
      discountAmount,
      taxAmount,
      total
    };
  };

  const updateInvoiceData = (field, value) => {
    setInvoiceData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Recalculate totals when line items or discount changes
      if (field === 'lineItems' || field === 'discountType' || field === 'discountValue') {
        const totals = calculateTotals(
          field === 'lineItems' ? value : updated?.lineItems,
          {
            type: field === 'discountType' ? value : updated?.discountType,
            value: field === 'discountValue' ? value : updated?.discountValue
          }
        );
        return { ...updated, ...totals };
      }
      
      return updated;
    });
  };

  const handleSave = (action = 'draft') => {
    // Validation
    if (!invoiceData?.customer) {
      alert('Please select a customer');
      return;
    }

    if (!invoiceData?.lineItems?.some(item => item?.description)) {
      alert('Please add at least one line item');
      return;
    }

    // Here you would typically save to your backend
    console.log('Saving invoice:', { ...invoiceData, status: action });
    
    switch (action) {
      case 'draft': alert('Invoice saved as draft');
        break;
      case 'send': alert('Invoice sent to customer');
        break;
      case 'schedule': alert('Invoice scheduled for sending');
        break;
      default:
        alert('Invoice saved');
    }
  };

  const handlePreview = () => {
    if (!invoiceData?.customer) {
      alert('Please select a customer to preview invoice');
      return;
    }
    setShowPreview(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex pt-16">
        <Sidebar 
          isCollapsed={isSidebarCollapsed} 
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
        />
        
        <main className="flex-1 overflow-auto">
          <div className="h-full flex">
            {/* Main Content Area */}
            <div className={cn(
              "flex-1 p-6 transition-all duration-300",
              showPreview ? "pr-3" : ""
            )}>
              {/* Page Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Create Invoice</h1>
                  <p className="text-muted-foreground mt-1">
                    Generate professional invoices with automated calculations and customer management
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    iconName="Eye"
                    iconPosition="left"
                    onClick={handlePreview}
                  >
                    Preview
                  </Button>
                  <Button
                    variant="outline"
                    iconName="Save"
                    iconPosition="left"
                    onClick={() => handleSave('draft')}
                  >
                    Save Draft
                  </Button>
                  <Button
                    variant="default"
                    iconName="Send"
                    iconPosition="left"
                    onClick={() => handleSave('send')}
                  >
                    Send Invoice
                  </Button>
                </div>
              </div>

              <div className="space-y-6">
                {/* Customer Selection */}
                <CustomerSelector
                  customer={invoiceData?.customer}
                  customerOptions={customerOptions}
                  onChange={(customer) => updateInvoiceData('customer', customer)}
                />

                {/* Invoice Details */}
                <InvoiceDetails
                  invoiceNumber={invoiceData?.invoiceNumber}
                  invoiceDate={invoiceData?.invoiceDate}
                  dueDate={invoiceData?.dueDate}
                  poNumber={invoiceData?.poNumber}
                  onChange={updateInvoiceData}
                />

                {/* Line Items */}
                <LineItemsTable
                  items={invoiceData?.lineItems}
                  productItems={productItems}
                  onChange={(items) => updateInvoiceData('lineItems', items)}
                />

                {/* Tax Calculation */}
                <TaxCalculationPanel
                  subtotal={invoiceData?.subtotal}
                  discountType={invoiceData?.discountType}
                  discountValue={invoiceData?.discountValue}
                  discountAmount={invoiceData?.discountAmount}
                  taxAmount={invoiceData?.taxAmount}
                  total={invoiceData?.total}
                  onDiscountChange={(type, value) => {
                    updateInvoiceData('discountType', type);
                    updateInvoiceData('discountValue', value);
                  }}
                />

                {/* Payment Terms */}
                <PaymentTermsSection
                  paymentTerms={invoiceData?.paymentTerms}
                  onChange={(terms) => updateInvoiceData('paymentTerms', terms)}
                />

                {/* Notes Section */}
                <NotesSection
                  notes={invoiceData?.notes}
                  termsAndConditions={invoiceData?.termsAndConditions}
                  onNotesChange={(notes) => updateInvoiceData('notes', notes)}
                  onTermsChange={(terms) => updateInvoiceData('termsAndConditions', terms)}
                />

                {/* Bottom Action Bar */}
                <div className="flex items-center justify-between pt-6 border-t bg-background sticky bottom-0">
                  <div className="flex items-center gap-4">
                    <Button
                      variant="ghost"
                      iconName="ArrowLeft"
                      iconPosition="left"
                      onClick={() => navigate('/sales-hub')}
                    >
                      Back to Sales Hub
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Total Amount</p>
                      <p className="text-xl font-bold text-foreground">
                        ${invoiceData?.total?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="w-px h-8 bg-border mx-2"></div>
                    <Button
                      variant="outline"
                      iconName="Clock"
                      iconPosition="left"
                    >
                      Schedule
                    </Button>
                    <Button
                      variant="default"
                      iconName="Send"
                      iconPosition="left"
                      onClick={() => handleSave('send')}
                    >
                      Send Invoice
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Preview Panel */}
            {showPreview && (
              <div className="w-96 border-l bg-background p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Invoice Preview</h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPreview(false)}
                    iconName="X"
                  />
                </div>
                <InvoicePreview
                  invoiceData={invoiceData}
                  onDownloadPDF={() => console.log('Download PDF')}
                  onSendEmail={() => handleSave('send')}
                />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default InvoiceCreation;